<template>
  <UiApp :link="NuxtLink" :labels="uiLabels">
    <Maintenance v-if="config.public.isMaintenanceEnabled" />
    <template v-else>
      <NuxtLayout>
        <NuxtLoadingIndicator />
        <NuxtPage />
        <Backdrop v-if="isLoading" class="z-[200]">
          <CircularSpinner />
        </Backdrop>
        <VariablesModal />
      </NuxtLayout>
    </template>
    <div id="teleports" />
  </UiApp>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports';
import { NuxtLink } from '#components';
import { App as UiApp, type UiLabels } from '@exfluential/ui';
import Maintenance from './maintenance.vue';
import { useFavicon } from '@vueuse/core';
import { useOrganizationStore } from '~/store/organization';
import { VariablesModal } from '~/pages/_components/variables-modal';
import { useHubStore } from '~/store/hub';
import { Backdrop } from '~/components/backdrop';
import { useLoaderStore } from '~/store/loader';
import { CircularSpinner } from '~/components/loaders';
import { apiGetActiveCookieConsentBanner } from '~/api/organizations';
import { useCookieConsent } from '~/composables/useCookieConsent';
import { usePostHog } from '~/composables/usePostHog';
import { watch, computed, ref } from 'vue';
import {
  WHITE,
  BLACK,
  clamp01,
  mixHex,
  adjustHsl,
  lightnessOf,
  perceivedBrightness,
  shadeHex,
} from '~/utils/colors';
import { useIsInIframe } from '~/composables/use-is-in-iframe';
import { captureHandledError } from '~/constants/sentry';

const IFRAME_BOTTOM_INSET_PX = 96;

const { t } = useI18n();
const uiLabels = computed<Partial<UiLabels>>(() => ({
  close: t('common.close'),
  cancel: t('common.cancel'),
  confirm: t('common.confirm'),
  delete: t('common.delete'),
  save: t('common.save'),
  apply: t('common.apply'),
  filters: t('filters-modal.filters'),
  resetFilters: t('filters-modal.reset-filters'),
  breadcrumbs: t('common.breadcrumbs'),
}));
const isInIframe = useIsInIframe();

const loaderStore = useLoaderStore();
const { isLoading } = storeToRefs(loaderStore);

const config = useRuntimeConfig();

const domain = useOrganizationDomain();
const { organization } = storeToRefs(useOrganizationStore());
const { hub } = storeToRefs(useHubStore());

const { syncPostHogConsent, teardown: teardownCookieConsent } =
  useCookieConsent();
const { optIn: optInPostHog, optOut: optOutPostHog } = usePostHog();

useHead({
  title: organization.value?.name,
});

const robotsMeta = computed(() => {
  if (
    domain !== null &&
    organization.value?.isSearchEngineIndexingEnabled === false
  ) {
    return [{ name: 'robots', content: 'noindex,nofollow' }];
  }
  return [];
});

useHead({
  meta: robotsMeta,
});

const rootColorVariables = computed<string>(() => {
  const theme = hub.value ?? organization.value;

  // Base anchor tokens — resolved to the theme hex (or default). Every derived
  // token below is computed in JS from one of these and emitted as literal hex,
  // recomputed by this computed() whenever a base color changes.
  const baseColors = [
    {
      name: '--background-color',
      value: theme === null ? '#F2F5FA' : theme.backgroundColor,
    },
    {
      name: '--on-background-color',
      value: theme === null ? '#042957' : theme.onBackgroundColor,
    },
    {
      name: '--on-background-variant-color',
      value: theme === null ? '#7086A4' : theme.onBackgroundVariantColor,
    },
    {
      name: '--surface-color',
      value: theme === null ? '#FFFFFF' : theme.surfaceColor,
    },
    {
      name: '--on-surface-color',
      value: theme === null ? '#042957' : theme.onSurfaceColor,
    },
    {
      name: '--on-surface-variant-color',
      value: theme === null ? '#7086A4' : theme.onSurfaceVariantColor,
    },
    {
      name: '--help-box-color',
      value: theme === null ? '#042957' : theme.helpBoxColor,
    },
    {
      name: '--on-help-box-color',
      value: theme === null ? '#FFFFFF' : theme.onHelpBoxColor,
    },
    {
      name: '--primary-color',
      value: theme === null ? '#427DFF' : theme.primaryColor,
    },
    {
      name: '--on-primary-color',
      value: theme === null ? '#042957' : theme.onPrimaryColor,
    },
    {
      name: '--secondary-color',
      value: theme === null ? '#042957' : theme.secondaryColor,
    },
    {
      name: '--on-secondary-color',
      value: theme === null ? '#7086A4' : theme.onSecondaryColor,
    },
    {
      name: '--tertiary-color',
      value: theme === null ? '#042957' : theme.tertiaryColor,
    },
    {
      name: '--on-tertiary-color',
      value: theme === null ? '#FFFFFF' : theme.onTertiaryColor,
    },
    {
      name: '--error-color',
      value: theme === null ? '#F62B2B' : theme.errorColor,
    },
    {
      name: '--success-color',
      value: theme === null ? '#34E3A8' : theme.successColor,
    },
    {
      name: '--info-color',
      value: theme === null ? '#3E6FED' : theme.infoColor,
    },
    {
      name: '--warning-color',
      value: theme === null ? '#F6B22B' : theme.warningColor,
    },
    {
      name: '--line-color',
      value: theme === null ? '#CFD9E4' : theme.lineColor,
    },
    {
      name: '--disabled-color',
      value: theme === null ? '#ECF0F4' : theme.disabledColor,
    },
  ];

  type ColorToken = { name: string; value: string };

  // Resolves a base anchor token back to its hex so every derived role can be
  // computed in JS from the actual (themed or default) color.
  const baseHex = (name: string): string =>
    baseColors.find((token) => token.name === name)?.value ?? BLACK;

  // Token names match the `--<role>-color` runtime vars that @exfluential/ui's
  // theme.css reads, e.g. `--color-primary-700: var(--primary-700-color, …)`.

  // Accent tonal scale (50–950): light steps mix the base toward white, dark
  // steps mix toward black, keeping hue.
  const accentScale = (name: string, base: string): ColorToken[] => {
    const hex = baseHex(base);
    const step = (token: string, target: string, fraction: number) => ({
      name: `--${name}-${token}-color`,
      value: mixHex(hex, target, fraction),
    });
    return [
      step('50', WHITE, 0.95),
      step('100', WHITE, 0.85),
      step('200', WHITE, 0.7),
      step('300', WHITE, 0.5),
      step('400', WHITE, 0.25),
      step('600', BLACK, 0.18),
      step('700', BLACK, 0.36),
      step('800', BLACK, 0.54),
      step('900', BLACK, 0.72),
      step('950', BLACK, 0.88),
    ];
  };

  // Accent interactive states (not in the spec): brightness-aware hover/active;
  // disabled is a light, desaturated tint.
  const accentStates = (name: string, base: string): ColorToken[] => {
    const hex = baseHex(base);
    return [
      { name: `--${name}-hover-color`, value: shadeHex(hex, 0.08) },
      { name: `--${name}-active-color`, value: shadeHex(hex, 0.15) },
      {
        name: `--${name}-disabled-color`,
        value: adjustHsl(hex, { l: 0.8, s: 0.25 }),
      },
    ];
  };

  // Status family: on / container / on-container / disabled, plus
  // brightness-aware hover/active so dark status colors stay visible.
  const statusFamily = (name: string, base: string): ColorToken[] => {
    const hex = baseHex(base);
    const onLightness = perceivedBrightness(hex) >= 0.5 ? 0.22 : 0.98;
    return [
      {
        name: `--on-${name}-color`,
        value: adjustHsl(hex, { l: onLightness, s: 0.1 }),
      },
      {
        name: `--${name}-container-color`,
        value: adjustHsl(hex, { l: 0.92, s: 0.8 }),
      },
      {
        name: `--on-${name}-container-color`,
        value: adjustHsl(hex, { l: 0.28 }),
      },
      {
        name: `--${name}-disabled-color`,
        value: adjustHsl(hex, { l: 0.8, s: 0.25 }),
      },
      { name: `--${name}-active-color`, value: shadeHex(hex, 0.15) },
      { name: `--${name}-hover-color`, value: shadeHex(hex, 0.08) },
    ];
  };

  // Surface containers, derived from the themeable surface color. theme.css
  // aliases the on-surface roles to the base on-surface vars, so they need no
  // separate injection here.
  const surfaceHex = baseHex('--surface-color');
  const surfaceTokens: ColorToken[] = [
    {
      name: '--surface-container-color',
      value: mixHex(surfaceHex, BLACK, 0.02),
    },
    {
      name: '--surface-container-hover-color',
      value: mixHex(surfaceHex, BLACK, 0.05),
    },
    {
      name: '--surface-container-disabled-color',
      value: adjustHsl(surfaceHex, {
        l: clamp01(lightnessOf(surfaceHex) - 0.04),
        s: 0.5,
      }),
    },
  ];

  // Focus ring follows the primary color. Neutral and indicator roles have no
  // themeable base field, so theme.css's static defaults are left to stand.
  const utilityTokens: ColorToken[] = [
    { name: '--focused-color', value: baseHex('--primary-color') },
  ];

  const themeColors: ColorToken[] = [
    ...baseColors,
    ...surfaceTokens,
    ...accentScale('primary', '--primary-color'),
    ...accentStates('primary', '--primary-color'),
    ...accentScale('secondary', '--secondary-color'),
    ...accentStates('secondary', '--secondary-color'),
    ...accentScale('tertiary', '--tertiary-color'),
    ...accentStates('tertiary', '--tertiary-color'),
    ...statusFamily('info', '--info-color'),
    ...statusFamily('success', '--success-color'),
    ...statusFamily('warning', '--warning-color'),
    ...statusFamily('error', '--error-color'),
    ...utilityTokens,
  ];

  const parsedVars = themeColors
    .map(({ name, value }) => `${name}: ${value};`)
    .join('\n');

  const iframeInset = `--iframe-bottom-inset: ${
    isInIframe.value ? `${IFRAME_BOTTOM_INSET_PX}px` : '0px'
  };`;

  return `:root {\n${parsedVars}\n${iframeInset}\n}`;
});

useHead({
  style: [{ textContent: rootColorVariables }],
});

if (
  domain !== null &&
  organization.value?.customDomain === domain &&
  organization.value?.iconUrl
) {
  useFavicon(organization.value.iconUrl);
}

const injectedElements: (HTMLScriptElement | Node)[] = [];

const loadCookieConsentBanner = async () => {
  if (process.server) {
    return;
  }

  if (!organization.value?.isCookieConsentEnabled) {
    for (const element of injectedElements) {
      if (element.parentNode) {
        element.remove();
      }
    }
    injectedElements.length = 0;
    return;
  }

  try {
    const activeBanner = await apiGetActiveCookieConsentBanner();
    if (!activeBanner?.script) return;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = activeBanner.script;

    const noscriptElements = tempDiv.querySelectorAll('noscript');
    for (const noscript of noscriptElements) {
      if (noscript.parentNode) {
        noscript.remove();
      }
    }

    const scripts = [...tempDiv.querySelectorAll('script')];
    const scriptPromises: Promise<void>[] = [];

    for (const script of scripts) {
      if (script.textContent && !script.src) {
        const scriptEl = document.createElement('script');
        if (script.type) scriptEl.type = script.type;
        scriptEl.textContent = script.textContent;
        document.body.append(scriptEl);
        injectedElements.push(scriptEl);
        if (script.parentNode) {
          script.remove();
        }
      }
    }

    for (const script of scripts) {
      if (script.src) {
        const promise = new Promise<void>((resolve) => {
          const scriptEl = document.createElement('script');
          scriptEl.src = script.src;
          if (script.type) scriptEl.type = script.type;
          if (script.charset) scriptEl.charset = script.charset;
          scriptEl.async = script.async || false;

          for (const attr of script.attributes) {
            if (attr.name.startsWith('data-')) {
              scriptEl.setAttribute(attr.name, attr.value);
            }
          }

          scriptEl.addEventListener('load', () => resolve());
          scriptEl.addEventListener('error', () => resolve());
          document.body.append(scriptEl);
          injectedElements.push(scriptEl);
        });
        scriptPromises.push(promise);
        if (script.parentNode) {
          script.remove();
        }
      }
    }

    while (tempDiv.firstChild) {
      const node = tempDiv.firstChild;
      document.body.append(node);
      injectedElements.push(node);
    }

    await Promise.all(scriptPromises);

    syncPostHogConsent();
  } catch (err) {
    captureHandledError(err, { feature: 'cookie-consent-banner' });
  }
};

if (import.meta.client) {
  watch(
    () => organization.value,
    () => {
      teardownCookieConsent();
      if (organization.value?.isCookieConsentEnabled) {
        optOutPostHog();
        if (!isInIframe.value) {
          loadCookieConsentBanner();
        }
      } else {
        loadCookieConsentBanner();
        if (organization.value) {
          optInPostHog();
        }
      }
    },
    { immediate: true, deep: true },
  );
}
</script>
